import React from "react";
import { THEME } from "./state";

export const NEXTGEN_AFFILIATE_URL =
  "https://my.thenextgenfunding.com/challenges?affiliateId=flexx";

/**
 * NextGenCTA — appears only at high-intent funding moments
 * (Capstone certificate earned, Phase 4 mastery gate passed,
 * inside the Advanced Course Prop Firm Path).
 *
 * variant:
 *   "celebration"  -> full gold card with title + bullets (post-cert / post-phase4)
 *   "inline"       -> compact inline strip (in-course)
 *   "verify"       -> lighter card for the public /verify page
 */
export default function NextGenCTA({ variant = "celebration", studentName = "" }) {
  if (variant === "inline") return <InlineStrip />;
  if (variant === "verify") return <VerifyCard studentName={studentName} />;
  return <CelebrationCard studentName={studentName} />;
}

function CelebrationCard({ studentName }) {
  const greet = studentName ? `${studentName.split(" ")[0]}, you're ready` : "You're ready";
  return (
    <div
      data-testid="nextgen-cta-celebration"
      className="flexx-fade-in"
      style={{
        marginTop: 18,
        padding: 22,
        borderRadius: 16,
        background: "linear-gradient(135deg, #1a1300 0%, #0d0d0d 60%)",
        border: `1px solid ${THEME.gold}80`,
        boxShadow: `0 4px 28px ${THEME.gold}25`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "absolute", top: -40, right: -40, width: 160, height: 160,
          background: `radial-gradient(circle, ${THEME.gold}30, transparent 70%)`,
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>
            FLEXX × NEXTGENFUNDING
          </span>
          <span
            style={{
              fontSize: 9, padding: "2px 8px", borderRadius: 999,
              background: `${THEME.gold}25`, color: THEME.gold, fontWeight: 700, letterSpacing: 1,
            }}
          >
            OFFICIAL PARTNER
          </span>
        </div>
        <h3 style={{ color: THEME.text, margin: "4px 0 8px", fontSize: 19, fontWeight: 800 }}>
          {greet} to trade real capital 🏆
        </h3>
        <p style={{ color: THEME.mute, fontSize: 13, lineHeight: 1.6, margin: "0 0 14px" }}>
          You've proven the discipline. Take the system live with FLEXX Academy's exclusive prop firm partner — and keep more of what you make.
        </p>
        <ul style={{ margin: "0 0 16px", paddingLeft: 18, color: THEME.text, fontSize: 13, lineHeight: 1.7 }}>
          <li><b style={{ color: THEME.gold }}>90% profit split</b> from day one — scaling to <b style={{ color: THEME.gold }}>100%</b> after the 16th payout</li>
          <li><b style={{ color: THEME.gold }}>Balance-based drawdown</b> — far more forgiving than equity-based</li>
          <li>Payouts every 8 trading days · MT5 compatible · $10k–$200k+ accounts</li>
        </ul>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <a
            data-testid="nextgen-cta-celebration-btn"
            href={NEXTGEN_AFFILIATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flexx-btn-primary"
            style={{
              textDecoration: "none",
              background: THEME.gold, color: "#0d0d0d", fontWeight: 700,
              padding: "10px 18px", fontSize: 13,
            }}
          >
            Get Funded with NextGenFunding →
          </a>
          <span style={{ color: THEME.mute, fontSize: 11 }}>
            Exclusive FLEXX partner perks · No financial advice
          </span>
        </div>
      </div>
    </div>
  );
}

function InlineStrip() {
  return (
    <div
      data-testid="nextgen-cta-inline"
      style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "12px 16px", borderRadius: 10,
        background: `${THEME.gold}10`,
        border: `1px solid ${THEME.gold}40`,
        margin: "12px 0",
      }}
    >
      <span
        style={{
          fontSize: 9, padding: "3px 8px", borderRadius: 999,
          background: `${THEME.gold}30`, color: THEME.gold, fontWeight: 800, letterSpacing: 1,
        }}
      >
        FLEXX PARTNER ★
      </span>
      <span style={{ color: THEME.text, fontSize: 13, flex: "1 1 auto", minWidth: 200 }}>
        Ready to challenge? FLEXX students get exclusive partner perks at NextGenFunding.
      </span>
      <a
        data-testid="nextgen-cta-inline-btn"
        href={NEXTGEN_AFFILIATE_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          background: THEME.gold, color: "#0d0d0d", fontWeight: 700,
          padding: "7px 14px", borderRadius: 8, fontSize: 12, textDecoration: "none",
          whiteSpace: "nowrap",
        }}
      >
        Start Challenge →
      </a>
    </div>
  );
}

function VerifyCard({ studentName }) {
  return (
    <div
      data-testid="nextgen-cta-verify"
      style={{
        marginTop: 16,
        padding: 18,
        borderRadius: 12,
        background: "#13161C",
        border: `1px solid ${THEME.gold}50`,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 9, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 4 }}>
        FLEXX × NEXTGENFUNDING · OFFICIAL PARTNER
      </div>
      <div style={{ color: THEME.text, fontSize: 14, lineHeight: 1.5, margin: "6px 0 12px" }}>
        {studentName ? `${studentName.split(" ")[0]} earned this certificate — ready for funded capital.` : "FLEXX-certified traders are funded-ready."} Get exclusive partner perks below.
      </div>
      <a
        data-testid="nextgen-cta-verify-btn"
        href={NEXTGEN_AFFILIATE_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "inline-block",
          background: THEME.gold, color: "#0d0d0d", fontWeight: 700,
          padding: "9px 18px", borderRadius: 8, fontSize: 13, textDecoration: "none",
        }}
      >
        Get Funded with NextGenFunding →
      </a>
    </div>
  );
}
